import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Search } from 'lucide-react'

import { useTranslation } from 'react-i18next'

import { EditableTagsReadOnly } from '@/components/EditableTagsReadOnly'
import { PhoneLink } from '@/components/PhoneLink'
import { SortDropdown } from '@/components/SortDropdown'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { track } from '@/lib/analytics'
import { formatDate } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { phoneMatches } from '@/lib/phone'
import type { Tag } from '@/lib/tags'
import { useListSort } from '@/lib/useListSort'

type CustomerListRow = {
  id: string
  name: string
  phone: string | null
  email: string | null
  tags: string[] | null
  tag_chips: Tag[]
  lifetime_value_cad: number
  last_visit: string | null
  vehicle_count: number
  vehicles_label: string
}

export function Customers() {
  const { t } = useTranslation('customers')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const { sort, dir, setSort, toggleDir } = useListSort('customers')

  const customersQuery = useQuery({
    queryKey: ['customers', sort, dir],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/customers?sort=${sort}&dir=${dir}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }

      return (await res.json()) as CustomerListRow[]
    },
  })

  const filtered = useMemo(() => {
    const data = customersQuery.data ?? []
    const raw = search.trim()
    if (!raw) return data
    // phoneMatches reduces both sides to digits-only and substring-checks,
    // so it kicks in for any query with at least one digit (area code,
    // partial number, full number) and is a no-op for letter-only input.
    // OR'd alongside the existing name / phone / email / vehicles
    // substring filters so a single predicate handles everything.
    const q = raw.toLowerCase()
    return data.filter((c) => {
      return (
        c.name.toLowerCase().includes(q) ||
        (c.phone ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.vehicles_label ?? '').toLowerCase().includes(q) ||
        phoneMatches(raw, c.phone)
      )
    })
  }, [customersQuery.data, search])

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle', { count: customersQuery.data?.length ?? 0 })}
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-3"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="search"
              placeholder={t('search.placeholder')}
              className="h-12 w-full rounded-[14px] pl-10"
            />
          </div>
          <SortDropdown
            value={sort}
            dir={dir}
            onChange={(next) => {
              if (next === sort) return
              track('list.sort_changed', {
                entity: 'customers',
                from: sort,
                to: next,
              })
              setSort(next)
            }}
            onToggleDir={() => {
              track('list.sort_dir_toggled', {
                entity: 'customers',
                sort,
                to: dir === 'asc' ? 'desc' : 'asc',
              })
              toggleDir()
            }}
            namespace="customers"
            className="sm:w-48"
          />
          <Button asChild size="pill">
            <Link to="/customers/new">{t('header.new')}</Link>
          </Button>
        </div>
      </div>

      {customersQuery.isError ? (
        <div className="rounded-[14px] border border-destructive/25 bg-destructive-soft p-4 text-sm text-destructive">
          {customersQuery.error instanceof Error
            ? customersQuery.error.message
            : t('error')}
        </div>
      ) : null}

      {filtered.length > 0 ? (
        <div className="overflow-hidden rounded-[14px] border border-border bg-background">
          {filtered.map((c) => {
            const chips = (c.tag_chips ?? []).slice(0, 3)
            // Wrapper is a role=button div (not <Link>) so we can nest a
            // real <a href="tel:"> via <PhoneLink> without invalid nested
            // anchors. Keyboard accessibility preserved with Enter/Space.
            const goToDetail = () => navigate(`/customers/${c.id}`)
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={goToDetail}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    goToDetail()
                  }
                }}
                className="flex min-h-[60px] cursor-pointer items-center gap-3 border-t border-border bg-background px-3.5 py-3 transition-[background-color,transform] first:border-t-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
              >
                <span className="inline-flex size-[38px] shrink-0 items-center justify-center rounded-full bg-surface-2 text-[13px] font-bold text-muted-foreground">
                  {c.name.charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="max-w-full truncate text-[15px] font-[620] text-foreground">
                      {c.name}
                    </span>
                    <EditableTagsReadOnly tags={chips} size="xs" />
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-muted-foreground">
                    <span className="font-mono tabular-nums">
                      <PhoneLink phone={c.phone} />
                    </span>
                    <span aria-hidden="true">·</span>
                    <span>{t('card.vehicle_count', { count: c.vehicle_count })}</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono text-[12px] font-[550] tabular-nums text-foreground">
                    {formatMoney(c.lifetime_value_cad)}
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {c.last_visit
                      ? t('card.last_visit', { date: formatDate(c.last_visit) })
                      : t('card.no_visits')}
                  </div>
                </div>
                <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-ink-3" />
              </div>
            )
          })}
        </div>
      ) : !customersQuery.isLoading ? (
        <div className="rounded-[14px] border border-border bg-background px-3.5 py-12 text-center text-[13px] text-muted-foreground">
          {t('empty')}
        </div>
      ) : null}
    </div>
  )
}
